import { next } from '@ember/runloop';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DChartInterface, DSSetup } from 'webapp-types';
import type { BlockVisualization, TableColumnType } from 'webapp/enums/enums';
import { checkToApplyWatchHack, copyObject, equal } from 'webapp/utils/common';

import { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';

import type Block from 'webapp/models-custom/block';
import type BoardState from 'webapp/models-custom/board-state';
import type { MasterMetricSetup } from 'webapp/models-custom/board-state';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type { SampleData } from 'webapp/models/sample';

// See ./board-state for more info

export interface SettingsChanges {
	visualization?: BlockVisualization;
	tableColumnType?: TableColumnType;
}

// the block preview badge also stores the master filter's dimensions / filters as given (null: explicitly none)
export type MetricSetupChanges = Omit<MasterMetricSetup, 'dimensions' | 'filters'> & {
	dimensions?: string[] | null;
	filters?: DSSetup.MetricSetupFiltersData | null;
};

export interface Changes {
	dateRange?: DateRangeInterface.FormulaInterface;
	metricSetup?: MetricSetupChanges;
	settings?: SettingsChanges;
}

export default class BlockState {
	@tracked block: Block;

	@tracked index = 0; // temporary layer change

	private _sampleData?: SampleData;

	get shownData() {
		const sampledata = this.block.sample?.sampledata;

		const sampleData = sampledata;

		if (!equal(this._sampleData, sampleData)) {
			this._sampleData = sampleData ? copyObject(sampleData) : sampleData; // copy, so we don't overwrite sample when switching layers (main chart)
		}

		return this._sampleData;
	}

	get actualData() {
		let shownData = this.shownData;

		// check daterange, if applicable
		if (shownData) {
			const dateRangeOnData = shownData.dateRange,
				dateRangeCMOnBlockState = this.limitationsCM?.dateRange;

			if (!dateRangeOnData || !dateRangeCMOnBlockState?.isEqual(dateRangeOnData)) {
				shownData = undefined;
			}
		}

		return shownData;
	}

	get isLoadingData() {
		const block = this.block;
		if (block.isText || block.isImage) {
			return false;
		} else if (block.isAiSummary) {
			return block.aiSummaryStatus === AiSummaryContentStatus.LOADING;
		} else if (this.actualData) {
			return false;
		} else if (!block.blockSetup?.dataSelectsShown?.length) {
			// dont show loader if no metrics chosen
			return false;
		} else if (!this.actualData) {
			return true;
		} else {
			// show the big loader if we have empty data with isSync statusObject
			return block.isDataEmpty && block.isSyncing;
		}
	}

	get isDataIncomplete() {
		return this.block.model.isGenerating || !!this.isLoadingData || this.block.isDataIncomplete;
	}

	boardState!: BoardState;

	id;

	changes: Changes = {}; // changes that are not saved to preview yet

	constructor(blockCM: Block) {
		this.id = blockCM.numericId;

		this.block = blockCM;
	}

	get annotationContext() {
		// used in in d-chart/tooltip/base contextUpdate()
		let context: DChartInterface.AnnotationContext | null = null;

		const boardState = this.boardState;
		if (boardState?.board && !boardState.board.isFake) {
			if (boardState.board.isPreview) {
				context = {
					type: 'boardPreview',
					id: boardState.board.previewHash!,
				};
			} else {
				context = {
					type: 'board',
					id: boardState.board.id!,
				};
			}
		}

		return context;
	}

	@tracked private _limitations?: MetricLimitations;

	private _dateRangeFormula?: DateRangeInterface.FormulaInterface;

	@cached
	get limitationsCM() {
		// copy of the one on the block model
		// but also checking for daterange changes here
		// we use this limitationsCM for block preview daterange picker

		if (this._limitations?.dateRange) {
			// access formula to establish dependency tracking - intentionally unused (task ref: https://app.asana.com/1/7582066485656/project/1180537054235286/task/1209413599046673)
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this._limitations.dateRange.formula;
		}

		let limitations = this._limitations;

		if (this.block.id) {
			const blockDateRange = this.block.dateRange;

			let blockIsNumber = false;

			const blockSetup = this.block.blockSetup;
			if (blockSetup) {
				blockIsNumber = !blockSetup.visualization.hasGranulation;
			}

			// create initial copy of the limitations
			if (!limitations) {
				const dateRange = DateRange.createFromFormula(blockDateRange);

				limitations = new MetricLimitations({
					fetchLimitationsOnInit: false, // will fetch when opening picker dropdowns
					isNumber: blockIsNumber,
					metrics: this.block.blockSetup?.metricDataArray || [],
					dateRange,
				});

				checkToApplyWatchHack('block-state:_limitations', () => {
					this._limitations = limitations;
				});

				this._dateRangeFormula = dateRange.formula;
			} else {
				// keep limitations up-to-date with block data

				next(() => {
					if (!limitations) {
						return;
					}
					if (limitations.isNumber !== blockIsNumber) {
						limitations.isNumber = blockIsNumber;
					}

					limitations.changeMetrics(this.block.blockSetup?.metricDataArray || []);

					if (
						!this._dateRangeFormula ||
						!limitations.dateRange?.isEqual(this._dateRangeFormula)
					) {
						const formula = limitations.dateRange?.formula;

						this._dateRangeFormula = formula;
						this.change('dateRange', formula);
					} else if (!limitations.dateRange.isEqual(blockDateRange)) {
						// avoiding error of updating the prop that was just read
						limitations.updateDateRangeFromFormula(blockDateRange);

						this._dateRangeFormula = blockDateRange;
					}
				});
			}
		}

		return limitations;
	}

	@cached
	get metricData() {
		// no duplicates
		const metricData: DSSetup.MetricData[] = [];
		this.block.metricDataArray.forEach((md) => {
			if (md && !metricData.find((md2) => equal(md, md2))) {
				metricData.push(md);
			}
		});
		return metricData;
	}

	@cached
	get metricSetupDataArray() {
		// with duplicates
		return this.block.blockSetup?.metricSetupDataArray || [];
	}

	@cached
	get allowAdvancedSetup() {
		return this.block.blockSetup?.allowAdvancedSetup || false;
	}

	@cached
	get allowAdvancedFilters() {
		return this.block.blockSetup?.allowAdvancedFilters || false;
	}

	updateVisualizationSetting(visualization: BlockVisualization | undefined) {
		this.updateSettings({ visualization });
	}

	updateTableColumnTypeSetting(tableColumnType: TableColumnType | undefined) {
		this.updateSettings({ tableColumnType });
	}

	updateMetricSetup(
		dimensions: string[] | null | undefined,
		filters: DSSetup.MetricSetupFiltersData | null,
		skipStateChange = false,
	) {
		// dimensions null means reset
		const isReset = dimensions === null;

		const value = isReset ? undefined : { dimensions, filters };

		this.change('metricSetup', value, skipStateChange);
	}

	updateSettings(changes: SettingsChanges, skipStateChange = false) {
		const settings = this.block.changes?.settings;

		let hasChanged = false;

		const newSettings = copyObject(settings || {});
		(
			Object.entries(changes) as [
				keyof SettingsChanges,
				SettingsChanges[keyof SettingsChanges],
			][]
		).forEach(([settingKey, value]) => {
			if (settings?.[settingKey] !== value) {
				if (value) {
					Object.assign(newSettings, { [settingKey]: value });
				} else {
					delete newSettings[settingKey];
				}
				hasChanged = true;
			}
		});

		if (hasChanged) {
			this.change('settings', newSettings, skipStateChange);
		}
	}

	private change<K extends keyof Changes>(prop: K, value: Changes[K], skipStateChange = false) {
		// pass no value (undefined) to reset to default
		this.changes[prop] = value;
		if (!skipStateChange) {
			this.triggerBoardStateChange();
		}
	}

	triggerBoardStateChange() {
		// overwritten from outside
	}
}
